import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { 
  MessageSquare, Check, X, Trash2, Eye, Clock, Filter,
  AlertCircle, CheckCircle, XCircle
} from 'lucide-react';

const CATEGORIES = [
  { id: 'discussions', label: 'Discussions', color: '#9333ea' },
  { id: 'support', label: 'Support Circle', color: '#ec4899' },
  { id: 'experiences', label: 'Experiences', color: '#14b8a6' },
  { id: 'questions', label: 'Questions', color: '#f59e0b' }
];

export default function ManageCommunity() {
  const { token } = useAuth();
  const { currentColors } = useTheme();
  const [posts, setPosts] = useState([]);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    loadPosts();
  }, [filterStatus]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const params = filterStatus ? `?status=${filterStatus}` : '';
      const response = await axios.get(`${API}/admin/community/posts${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to load posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePostStatus = async (postId, status) => {
    try {
      await axios.put(
        `${API}/admin/community/posts/${postId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadPosts();
    } catch (error) {
      console.error('Failed to update post status:', error);
      alert('Failed to update post status');
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/admin/community/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadPosts();
      setSelectedPost(null);
    } catch (error) {
      console.error('Failed to delete post:', error);
      alert('Failed to delete post');
    }
  };

  const getCategoryInfo = (categoryId) => {
    return CATEGORIES.find(c => c.id === categoryId) || CATEGORIES[0];
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'rejected': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return { bg: 'rgba(34, 197, 94, 0.2)', text: '#22c55e' };
      case 'rejected': return { bg: 'rgba(239, 68, 68, 0.2)', text: '#ef4444' };
      default: return { bg: 'rgba(234, 179, 8, 0.2)', text: '#eab308' };
    }
  };

  return (
    <div className="space-y-6" data-testid="manage-community-page">
      {/* Header */}
      <div className="glass-card p-6 relative overflow-hidden">
        <div className="absolute inset-0 sacred-geometry opacity-20"></div>
        <div className="relative z-10 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <MessageSquare className="w-8 h-8" style={{ color: currentColors.primary }} />
              <h1 className="text-2xl font-cinzel" style={{ color: currentColors.primary }}>
                Community Moderation
              </h1>
            </div>
            <p style={{ color: currentColors.textMuted }}>
              Review and moderate community posts
            </p>
          </div>
          
          {/* Stats */}
          <div className="flex gap-4">
            <div className="text-center">
              <p className="text-2xl font-cinzel" style={{ color: '#eab308' }}>
                {posts.filter(p => p.status === 'pending').length}
              </p>
              <p className="text-xs" style={{ color: currentColors.textMuted }}>Pending</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['pending', 'approved', 'rejected', null].map(status => (
          <button
            key={status || 'all'}
            onClick={() => setFilterStatus(status)}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
            style={{
              backgroundColor: filterStatus === status ? `${currentColors.primary}20` : 'transparent',
              color: filterStatus === status ? currentColors.primary : currentColors.textMuted,
              border: `1px solid ${filterStatus === status ? currentColors.primary : currentColors.border}`
            }}
          >
            {status && getStatusIcon(status)}
            {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'All'}
          </button>
        ))}
      </div>

      {/* Posts Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-4 rounded-full animate-spin" 
               style={{ borderColor: `${currentColors.primary}30`, borderTopColor: currentColors.primary }}></div>
        </div>
      ) : posts.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#22c55e' }} />
          <h3 className="text-xl font-cinzel mb-2" style={{ color: currentColors.text }}>
            All caught up!
          </h3>
          <p style={{ color: currentColors.textMuted }}>
            No {filterStatus || ''} posts to review.
          </p>
        </div>
      ) : (
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `1px solid ${currentColors.border}` }}>
                  <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Post</th>
                  <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Author</th>
                  <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Category</th>
                  <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Status</th>
                  <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Date</th>
                  <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map(post => {
                  const category = getCategoryInfo(post.category);
                  const statusColor = getStatusColor(post.status);
                  return (
                    <tr 
                      key={post.id}
                      style={{ borderBottom: `1px solid ${currentColors.border}` }}
                      className="hover:opacity-80"
                    >
                      <td className="py-3 px-4">
                        <div className="max-w-xs">
                          <p className="font-medium truncate" style={{ color: currentColors.text }}>
                            {post.title}
                          </p>
                          <p className="text-sm truncate" style={{ color: currentColors.textMuted }}>
                            {post.content}
                          </p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm" style={{ color: currentColors.text }}>
                        {post.author_name}
                      </td>
                      <td className="py-3 px-4">
                        <span 
                          className="text-xs px-2 py-1 rounded-full"
                          style={{ backgroundColor: `${category.color}20`, color: category.color }}
                        >
                          {category.label}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span 
                          className="text-xs px-2 py-1 rounded-full flex items-center gap-1 w-fit"
                          style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
                        >
                          {getStatusIcon(post.status)}
                          {post.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm" style={{ color: currentColors.textMuted }}>
                        {formatDate(post.created_at)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-1">
                          <button
                            onClick={() => setSelectedPost(post)}
                            className="p-2 rounded-lg transition-all hover:opacity-70"
                            style={{ color: currentColors.accent }}
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {post.status !== 'approved' && (
                            <button
                              onClick={() => updatePostStatus(post.id, 'approved')}
                              className="p-2 rounded-lg transition-all hover:opacity-70 text-green-500"
                              title="Approve"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                          {post.status !== 'rejected' && (
                            <button
                              onClick={() => updatePostStatus(post.id, 'rejected')}
                              className="p-2 rounded-lg transition-all hover:opacity-70 text-red-400"
                              title="Reject"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => deletePost(post.id)}
                            className="p-2 rounded-lg transition-all hover:opacity-70 text-red-500"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Post Detail Modal */}
      {selectedPost && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div 
            className="glass-card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: currentColors.background }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span 
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{ 
                    backgroundColor: `${getCategoryInfo(selectedPost.category).color}20`, 
                    color: getCategoryInfo(selectedPost.category).color 
                  }}
                >
                  {getCategoryInfo(selectedPost.category).label}
                </span>
                <span 
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{ 
                    backgroundColor: getStatusColor(selectedPost.status).bg, 
                    color: getStatusColor(selectedPost.status).text 
                  }}
                >
                  {selectedPost.status}
                </span>
              </div>
              <button onClick={() => setSelectedPost(null)} style={{ color: currentColors.textMuted }}>
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm" style={{ color: currentColors.textMuted }}>
                By {selectedPost.author_name} • {formatDate(selectedPost.created_at)}
              </p>
            </div>
            
            <h2 className="text-xl font-cinzel mb-4" style={{ color: currentColors.primary }}>
              {selectedPost.title}
            </h2>
            
            <div 
              className="p-4 rounded-lg mb-6 whitespace-pre-wrap"
              style={{ backgroundColor: `${currentColors.primary}05`, color: currentColors.text }}
            >
              {selectedPost.content}
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-2">
              {selectedPost.status !== 'approved' && (
                <button
                  onClick={() => { updatePostStatus(selectedPost.id, 'approved'); setSelectedPost(null); }}
                  className="flex-1 py-3 rounded-lg flex items-center justify-center gap-2 transition-all"
                  style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }}
                >
                  <Check className="w-5 h-5" />
                  Approve
                </button>
              )}
              {selectedPost.status !== 'rejected' && (
                <button
                  onClick={() => { updatePostStatus(selectedPost.id, 'rejected'); setSelectedPost(null); }}
                  className="flex-1 py-3 rounded-lg flex items-center justify-center gap-2 transition-all"
                  style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' }}
                >
                  <X className="w-5 h-5" />
                  Reject
                </button>
              )}
              <button
                onClick={() => deletePost(selectedPost.id)}
                className="py-3 px-6 rounded-lg flex items-center justify-center gap-2 transition-all"
                style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
